import { TGLink, alphaLinks, socialLinks } from "app/config";
import useAppNavigation from "app/hooks/useAppNavigate";
import useWidth from "app/hooks/useWidth";
import { Path } from "app/constants/paths";
import { useTheme } from "app/shared";
import { ReactComponent as DiscordIcon } from "assets/img/icons/discordDark.svg";
import { ReactComponent as LinkedIn } from "assets/img/icons/linkedin.svg";
import { ReactComponent as LogoutBtn } from "assets/img/icons/logoutBtn.svg";
import { ReactComponent as Claim } from "assets/img/icons/sidebarIcons/MMService.svg";
import { ReactComponent as ForProject } from "assets/img/icons/sidebarIcons/branding.svg";
import { ReactComponent as Bridge } from "assets/img/icons/sidebarIcons/bridge.svg";
import { ReactComponent as ClaimPortal } from "assets/img/icons/sidebarIcons/claimPortal.svg";
import { ReactComponent as DocsIcon } from "assets/img/icons/sidebarIcons/docs.svg";
import { ReactComponent as HomeIcon } from "assets/img/icons/sidebarIcons/home.svg";
import { ReactComponent as LounchpadIcon } from "assets/img/icons/sidebarIcons/launchpads.svg";
import { ReactComponent as LogoUrl } from "assets/img/icons/sidebarIcons/logo_alphamind.svg";
import newIcon from "assets/img/icons/sidebarIcons/new.svg";
import { ReactComponent as NewsIcon } from "assets/img/icons/sidebarIcons/news.svg";
import { ReactComponent as QuestIcon } from "assets/img/icons/sidebarIcons/quest.svg";
import { ReactComponent as NFTIcon } from "assets/img/icons/sidebarIcons/swap.svg";
import { ReactComponent as TgBot } from "assets/img/icons/sidebarIcons/tgBot.svg";

import { ReactComponent as Tg } from "assets/img/icons/tgDark.svg";
import { ReactComponent as XIcon } from "assets/img/icons/xDark.svg";
import { ReactComponent as YTIcon } from "assets/img/icons/youtube.svg";
import { FC, useEffect, useRef, useState } from "react";
import { HiBars3 } from "react-icons/hi2";
import { LiaTimesSolid } from "react-icons/lia";
import { Link } from "react-router-dom";
import { useAccount, useDisconnect } from "wagmi";
import Button from "../button/button";

import { useAuth } from "app/hooks";
import styles from "./dashboard-sidebar.module.scss";
interface sidebarItemProps {
  item: any;
  title: string;
  link: string;
  newTag?: boolean;
}

const DashboardSidebar: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { address } = useAccount();
  const { navigateToLink, navigateToBuildKarma } = useAppNavigation();
  const { isSmallDesktop, isTablet, isMobile } = useWidth();
  const { disconnect } = useDisconnect();
  const toggleSidebar = () => setIsOpen(!isOpen);
  const isAuth = useAuth();

  const sidebarItems: sidebarItemProps[] = [
    {
      item: <HomeIcon color={theme === "dark" ? "#fff" : "#000"} />,
      title: "Home",
      link: Path.APP_MAIN,
    },
    {
      item: <LounchpadIcon color={theme === "dark" ? "#fff" : "#000"} />,
      title: "Dashboard",
      link: Path.DASHBOARD,
    },
    {
      item: <QuestIcon color={theme === "dark" ? "#fff" : "#000"} />,
      title: "Quests",
      link: Path.BUILD_KARMA,
    },
    {
      item: <ClaimPortal color={theme === "dark" ? "#fff" : "#000"} />,
      title: "Claim Portal",
      link: Path.CLAIM,
    },
    {
      item: <Bridge color={theme === "dark" ? "#fff" : "#000"} />,
      title: "Swap & Bridge",
      link: Path.BRIDGE,
      newTag: true,
    },
    ...(isAuth
      ? [
          {
            item: <Claim color={theme === "dark" ? "#fff" : "#000"} />,
            title: "Referral Statistics",
            link: Path.REF_STATISTICS,
            newTag: true,
          },
        ]
      : []),
  ];

  const handleClickOutside = (event: Event) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <div onClick={toggleSidebar}>
        {isOpen ? (
          <div className={styles.closeIcon}>
            {theme === "dark" ? (
              <LiaTimesSolid color="#fff" />
            ) : (
              <LiaTimesSolid color="#000" />
            )}
          </div>
        ) : (
          <div className={styles.burgerIcon}>
            {theme === "dark" ? <HiBars3 color="#fff" /> : <HiBars3 />}
          </div>
        )}
      </div>
      <div
        ref={sidebarRef}
        className={`${styles.sidebar} ${isOpen ? styles.open : ""} ${
          theme === "dark" ? styles.darkTheme : ""
        }`}
      >
        <div>
          <Link className={styles.logo} to={Path.DASHBOARD}>
            <LogoUrl color={theme === "dark" ? "#fff" : "#000"} />
          </Link>
          <div className={styles.sidebar_wrapper}>
            <ul className={styles.menu}>
              {sidebarItems.map((e) => (
                <li key={e.title} className={styles.item}>
                  {e.link === Path.BUILD_KARMA ? (
                    <button
                      className={styles.link}
                      onClick={(ev) => {
                        ev.preventDefault();
                        // Use navigation helper to preserve query params (invite/utm)
                        navigateToBuildKarma();
                        setIsOpen(false);
                      }}
                    >
                      {e.item}
                      <p className={styles.title}>{e.title}</p>
                      {e.newTag && (
                        <img className={styles.new} src={newIcon} alt="new" />
                      )}
                    </button>
                  ) : (
                    <Link to={e.link}>
                      {e.item}
                      <p className={styles.title}>{e.title}</p>
                      {e.newTag && (
                        <img className={styles.new} src={newIcon} alt="new" />
                      )}
                    </Link>
                  )}
                </li>
              ))}
              <li className={styles.item}>
                <a
                  href={alphaLinks.NFT}
                  target="_blank"
                  className={styles.link}
                >
                  <NFTIcon color={theme === "dark" ? "#fff" : "#000"} />
                  NFT
                  <img className={styles.new} src={newIcon} alt="new" />
                </a>
              </li>
              <li className={styles.item}>
                <a href={alphaLinks.HOME} target="_blank">
                  <DocsIcon color={theme === "dark" ? "#fff" : "#000"} />
                  About
                </a>
              </li>
              <li className={styles.item}>
                <a href={alphaLinks.APPLICATION} target="_blank">
                  <ForProject color={theme === "dark" ? "#fff" : "#000"} />
                  For Projects
                </a>
              </li>
              <li className={styles.item}>
                <a href={alphaLinks.BLOG} target="_blank">
                  <NewsIcon color={theme === "dark" ? "#fff" : "#000"} />
                  Blog
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className={styles.bottomSection}>
          <div className={styles.socialIcons}>
            <a
              href={socialLinks.TELEGRAM}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Tg color={theme === "dark" ? "#fff" : "#000"} />
            </a>
            <a
              href={socialLinks.TWITTER}
              target="_blank"
              rel="noopener noreferrer"
            >
              <XIcon color={theme === "dark" ? "#fff" : "#000"} />
            </a>
            <a
              href={socialLinks.DISCORD}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DiscordIcon color={theme === "dark" ? "#fff" : "#000"} />
            </a>
            <a
              href={socialLinks.YOUTUBE}
              target="_blank"
              rel="noopener noreferrer"
            >
              <YTIcon color={theme === "dark" ? "#fff" : "#000"} />
            </a>
            <a
              href={socialLinks.LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedIn color={theme === "dark" ? "#fff" : "#000"} />
            </a>
            <button
              className={styles.tgbot}
              onClick={navigateToLink(`${TGLink}${address}`)}
            >
              <TgBot color={theme === "dark" ? "#fff" : "#000"} />
            </button>
          </div>
          <div className={styles.policies}>
            {(isSmallDesktop || isMobile || isTablet) && (
              <Button
                variant="pink"
                className={styles.logoutBtn}
                onClick={() => disconnect()}
              >
                <LogoutBtn />
              </Button>
            )}
            <a
              href={socialLinks.PRIVACY_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            <a
              href={socialLinks.TERMS_OF_SERVICE}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
          </div>
          <div className={styles.copyright}>
            <p>&copy; 2025 Alphamind</p>
            <p>All rights reserved</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
